<template>
  <v-container>
    <v-row align="baseline" dense>
      <v-spacer />
      <v-col cols="12" class="text-right">
        <v-btn variant="text" class="mr-5" @click="onClose"> Cancelar </v-btn>
        <v-btn
          color="primary"
          class="mr-2"
          @click="onSubmit"
          :loading="isSubmitting"
        >
          Salvar
        </v-btn>
      </v-col>
    </v-row>
    <v-row>
      <v-col cols="12">
        <basic-company-info-form
          ref="infoForm"
          :form="form"
          :tenant-id="tenantId"
          @errors="hasInfoErrors = $event"
        />
      </v-col>
      <v-col cols="12">
        <company-address-form
          ref="addressForm"
          :form="form"
          @errors="hasAddressErrors = $event"
        />
      </v-col>
      <v-col cols="12" v-if="!tenantId">
        <company-admins
          ref="admins"
          :form="form"
          @errors="hasAdminErrors = $event"
        />
      </v-col>
    </v-row>
  </v-container>
</template>

<script setup>
import { ref, computed } from "vue";
import { useRoute, useRouter } from "vue-router";
import BasicCompanyInfoForm from "@/views/company/BasicCompanyInfoForm";
import CompanyAddressForm from "@/views/company/CompanyAddressForm";
import CompanyAdmins from "@/views/company/CompanyAdmins";
import { emptyToNull, onlyNumbers } from "@/utils";
import { useCreateCompanyMutation } from "@/queries/modules/companies";
import { useSnackbar } from "@/plugins/snackbar";
import { useMetaStore } from "@/stores/meta";

const route = useRoute();
const router = useRouter();
const snackbar = useSnackbar();
const metaStore = useMetaStore();

// Set page title
metaStore.setTitle("Nova Empresa");

// Refs
const infoForm = ref(null);
const addressForm = ref(null);
const admins = ref(null);
const hasInfoErrors = ref(false);
const hasAddressErrors = ref(false);
const hasAdminErrors = ref(false);
const form = ref({
  hubType: null,
  razao_social: "",
  nome_fantasia: "",
  phone: "",
  cnpj: "",
  cep: "",
  neighborhood: "",
  state_inscription: "",
  municipal_inscription: "",
  street: "",
  city: "",
  complement: "",
  state: "",
  number: "",
  company_type: null,
  codigo_municipio: "",
  csc: "",
  id_csc: "",
  sefazKey: "",
  cnae: "",
  codigo_regime_tributario: "",
  certificate: {
    file: null,
    password: "",
  },
  admin_emails: [
    {
      email: "",
    },
  ],
});

// Computed
const tenantId = computed(() => {
  return route.params.tenant_id ? Number(route.params.tenant_id) : null;
});

// TanStack Query - mutation
const createCompanyMutation = useCreateCompanyMutation();
const isSubmitting = computed(() => createCompanyMutation.isPending.value);

// Methods
const getFormData = () => {
  const formData = {
    tenant_id: tenantId.value,
    company_type: form.value.company_type,
    razao_social: form.value.razao_social,
    nome_fantasia: form.value.nome_fantasia,
    phone: form.value.phone,
    cnpj: onlyNumbers(form.value.cnpj),
    email: form.value.email,
    state_inscription: onlyNumbers(form.value.state_inscription),
    municipal_inscription: onlyNumbers(form.value.municipal_inscription),
    sefaz_security_key: form.value.sefazKey,
    cnae: onlyNumbers(form.value.cnae),
    csc: emptyToNull(form.value.csc),
    id_csc: emptyToNull(form.value.id_csc),
    zipcode: form.value.cep,
    city: form.value.city,
    complement: form.value.complement,
    neighborhood: form.value.neighborhood,
    number: form.value.number,
    street: form.value.street,
    uf: form.value.state,
    codigo_municipio: form.value.codigo_municipio,
    admin_emails: form.value.admin_emails.map((item) => item.email),
  };

  if (tenantId.value) {
    formData.company_type = "retail";
  }

  return formData;
};

const onClose = async () => {
  if (tenantId.value) {
    router.push({
      name: "tenant.detail",
      params: {
        tenant_id: tenantId.value,
      },
    });
  } else {
    router.push({
      name: "tenant.index",
    });
  }
};

const onSubmit = async () => {
  const validations = [infoForm.value.validate(), addressForm.value.validate()];
  if (route.meta.type === "tenant") {
    validations.push(admins.value.validate());
  }
  const results = await Promise.all(validations);
  const allValid = results.every(Boolean);

  if (!allValid) {
    snackbar.warning("Preencha os campos obrigatórios antes de salvar.");
    return;
  }

  try {
    const response = await createCompanyMutation.mutateAsync({
      company: getFormData(),
      tenantId: tenantId.value,
    });
    snackbar.success("Empresa criada com sucesso!");
    if (tenantId.value) {
      router.push({
        name: "tenant.detail",
        params: { tenant_id: tenantId.value },
      });
    } else {
      router.push({
        name: "tenant.detail",
        params: { tenant_id: response.id },
      });
    }
  } catch (error) {
    snackbar.error(`Ocorreu um erro ao cadastrar a empresa: ${error.message}`);
  }
};
</script>
